import { legalVars, type LegalDoc, type LegalVars } from "./types";

/** 《隐私政策》正文。改动要同步申报材料 docx(同一份文本导出)。 */
export function getPrivacyPolicy(
  {
    company: LEGAL_COMPANY,
    product: LEGAL_PRODUCT,
    address: LEGAL_ADDRESS,
  }: LegalVars = legalVars()
): LegalDoc {
  return {
    title: `${LEGAL_PRODUCT}隐私政策`,
    updatedAt: "2026年9月28日",
    intro: [
      `${LEGAL_COMPANY}（以下简称“我们”）是“${LEGAL_PRODUCT}”企业级AI应用平台（以下简称“平台”）的运营者。我们深知个人信息对您的重要性，将依据《中华人民共和国个人信息保护法》《中华人民共和国数据安全法》《中华人民共和国网络安全法》《生成式人工智能服务管理暂行办法》等法律法规，按照合法、正当、必要和诚信原则处理您的个人信息。`,
      "本政策适用于您通过网页端、企业微信应用及其他我们提供的入口使用平台服务的全过程。请您在使用平台前仔细阅读本政策，尤其是第二部分中单独说明的敏感个人信息处理规则。您登录或继续使用平台，即表示您已阅读并同意本政策。",
    ],
    sections: [
      {
        heading: "一、适用范围与角色说明",
        blocks: [
          "平台以企业（以下简称“租户”）为单位提供服务。租户的管理员为其员工开通账号并分配权限，员工使用账号登录平台。",
          "对于平台账号本身及平台运行所需的信息，我们作为个人信息处理者依本政策处理；对于租户上传到平台、或通过平台接入的企业微信等渠道产生的其租户客户（终端消费者）相关信息，租户是个人信息处理者，我们作为受托方，依照与租户的约定和租户的指示处理，并协助租户履行其法定义务。",
        ],
      },
      {
        heading: "二、我们收集的个人信息",
        blocks: [
          "我们仅收集实现平台功能所必需的信息，具体如下：",
          {
            list: [
              "账号信息：由租户管理员为您创建账号时填写的姓名、登录账号、手机号或邮箱、所属部门与角色。您的登录密码仅以不可逆加密方式存储，我们无法查看明文。",
              "登录与安全信息：登录时间、IP地址、浏览器类型等设备信息，以及您在平台中的关键操作记录（审计日志），用于账号安全、异常登录识别和问题排查。",
              "业务内容信息：您为使用AI功能而输入的指令与文字、上传的文件（如企业资料、产品图片、品牌素材）、以及平台为您生成的文本、图片、音频、视频等内容。",
              "企业微信接入信息：租户授权接入企业微信后，平台会获取为完成AI客服、销售陪练等功能所需的员工企业微信标识与姓名，以及经由企业微信客服渠道产生的会话内容。",
              "用量与账单信息：各功能的调用记录、积分消耗与充值记录，用于计费结算与对账。",
            ],
          },
          "敏感个人信息：当您使用数字人分身、声音复刻、真人出镜等功能时，需要上传您或他人的人脸图像、视频或声音样本。人脸与声纹属于敏感个人信息，我们仅在您主动上传并单独确认授权后处理，且仅用于生成您指定的内容。若素材涉及他人，您应事先取得该本人的书面授权（平台提供《肖像及声音使用授权书》模板）。",
          "公开信息：智能获客等功能会依据您设定的条件检索互联网上已公开的企业信息与公开内容，我们仅在合理范围内处理，并对处理结果中涉及的个人信息采取必要的保护措施。",
        ],
      },
      {
        heading: "三、我们如何使用个人信息",
        blocks: [
          {
            list: [
              "提供平台服务：账号登录与权限管理，执行您发起的内容生成、知识库问答、AI客服、销售陪练、经营诊断、智能获客等功能。",
              "保障安全：身份验证、首次登录强制修改密码、异常登录识别、操作审计、故障排查与服务稳定性监测。",
              "计费与客户服务：按用量结算积分、生成账单、响应您的咨询与售后请求。",
              "改进服务：基于去标识化的统计数据分析功能使用情况与失败原因，用于优化产品质量。",
            ],
          },
          "我们不会将您上传的业务内容和生成内容用于训练通用模型，不会将其提供给其他租户，也不会用于与您的请求无关的目的。如需将个人信息用于本政策未载明的其他目的，我们会事先征得您的同意。",
        ],
      },
      {
        heading: "四、委托处理、共享与转移",
        blocks: [
          "为实现平台功能，我们需要委托以下类型的合作方处理必要的数据，并与其约定数据保护义务，要求其仅在委托范围内处理：",
          {
            list: [
              "云计算服务提供方：提供服务器、数据库、对象存储等基础设施，平台数据存储于中华人民共和国境内的云服务器。",
              "大模型及生成服务提供方：为完成文本、图像、视频、语音的理解与生成，平台会将完成该次任务所必需的指令与素材传输给相应的模型服务提供方。",
              "企业微信（腾讯）：在租户授权接入后，用于消息收发与身份识别。",
              "公开数据服务提供方：用于智能获客功能中的公开信息检索。",
            ],
          },
          "除上述情形及法律法规另有规定外，我们不会向第三方出售、出租或共享您的个人信息。如因合并、分立、收购等原因需要转移个人信息，我们会告知您接收方的名称和联系方式，并要求接收方继续履行本政策义务。",
          "如某项功能需要向中华人民共和国境外提供个人信息，我们将依法履行个人信息出境的相关程序，并在向您告知境外接收方的名称、联系方式、处理目的与方式等事项后取得您的单独同意。",
        ],
      },
      {
        heading: "五、个人信息的存储",
        blocks: [
          "我们在中华人民共和国境内收集和产生的个人信息存储于境内。",
          "我们仅在实现处理目的所必需的最短期限内保存您的个人信息：账号信息在账号存续期间保存；安全日志与审计记录依据法律法规要求的期限保存；业务内容与生成内容在租户服务期间保存，租户可随时删除。超出保存期限或租户终止服务后，我们将删除或匿名化处理相关信息，法律法规另有规定的除外。",
        ],
      },
      {
        heading: "六、我们如何保护个人信息",
        blocks: [
          {
            list: [
              "传输安全：平台全站采用HTTPS加密传输。",
              "隔离与权限：租户之间数据逻辑隔离；租户内部按组织架构与角色分级授权，员工只能访问其权限范围内的数据；平台运营人员访问租户数据须经授权并留有审计记录。",
              "账号安全：密码加密存储；新账号首次登录须修改初始密码；账号在别处登录、被停用或密码变更时，原登录会话自动失效。",
              "运行保障：服务运行状态持续监控，数据定期备份，建立安全事件应急预案。",
            ],
          },
          "如不幸发生个人信息安全事件，我们将按照法律法规的要求及时告知您事件的基本情况、可能的影响、我们已采取或将要采取的处置措施，并向监管部门报告。",
        ],
      },
      {
        heading: "七、您的权利",
        blocks: [
          "您依法享有查阅、复制、更正、补充、删除个人信息，撤回同意，以及要求我们解释个人信息处理规则等权利：",
          {
            list: [
              "查阅与更正：您可以在平台“个人中心”查看和修改个人资料与登录密码；组织、角色等信息可联系租户管理员更正。",
              "删除：您可以在平台中删除自己上传的素材与生成内容；知识库等租户共有数据由租户管理员管理。",
              "撤回授权：您可以随时删除已上传的人脸、声音样本及由其建立的数字人形象与声音，撤回后我们不再处理，但不影响撤回前基于授权已进行的处理。",
              "注销账号：员工账号由租户管理员停用或删除；租户整体终止服务可联系我们办理。",
            ],
          },
          "对于租户作为处理者的信息（如其客户的会话内容），您可向相应租户提出请求，我们将协助租户响应。我们将在收到您的请求并验证身份后十五个工作日内答复。",
        ],
      },
      {
        heading: "八、未成年人保护",
        blocks: [
          "平台是面向企业及其员工的服务，不面向未满十四周岁的未成年人。如我们发现在未事先取得监护人同意的情况下处理了未成年人的个人信息，将尽快删除相关数据。",
        ],
      },
      {
        heading: "九、本政策的更新",
        blocks: [
          "我们可能适时修订本政策。修订后的政策将在平台公布，并以页面提示等方式告知您；对于重大变更，我们会以更显著的方式通知。若您在政策更新后继续使用平台，即表示您同意更新后的政策。",
        ],
      },
      {
        heading: "十、联系我们",
        blocks: [
          `如您对本政策或个人信息处理有任何疑问、意见或投诉，可通过平台内的售后服务群、您的客户经理，或书面致函与我们联系：${LEGAL_COMPANY}，地址：${LEGAL_ADDRESS}。`,
          "如您对我们的答复不满意，您还可以向网信、市场监管、公安等监管部门投诉或举报。",
        ],
      },
    ],
  };
}
